import axios from "axios";
import toast from "react-hot-toast";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Action Types
export const CREATE_OFFENSE_REQUEST = "CREATE_OFFENSE_REQUEST";
export const CREATE_OFFENSE_SUCCESS = "CREATE_OFFENSE_SUCCESS";
export const CREATE_OFFENSE_FAILURE = "CREATE_OFFENSE_FAILURE";

export const FETCH_OFFENSES_REQUEST = "FETCH_OFFENSES_REQUEST";
export const FETCH_OFFENSES_SUCCESS = "FETCH_OFFENSES_SUCCESS";
export const FETCH_OFFENSES_FAILURE = "FETCH_OFFENSES_FAILURE";

export const UPDATE_OFFENSE_REQUEST = "UPDATE_OFFENSE_REQUEST";
export const UPDATE_OFFENSE_SUCCESS = "UPDATE_OFFENSE_SUCCESS";
export const UPDATE_OFFENSE_FAILURE = "UPDATE_OFFENSE_FAILURE";

export const DELETE_OFFENSE_REQUEST = "DELETE_OFFENSE_REQUEST";
export const DELETE_OFFENSE_SUCCESS = "DELETE_OFFENSE_SUCCESS";
export const DELETE_OFFENSE_FAILURE = "DELETE_OFFENSE_FAILURE";

export const UPLOAD_OFFENSE_REQUEST = "UPLOAD_OFFENSE_REQUEST";
export const UPLOAD_OFFENSE_SUCCESS = "UPLOAD_OFFENSE_SUCCESS";
export const UPLOAD_OFFENSE_FAILURE = "UPLOAD_OFFENSE_FAILURE";

export const DOWNLOAD_TEMPLATE_REQUEST = "DOWNLOAD_TEMPLATE_REQUEST";
export const DOWNLOAD_TEMPLATE_SUCCESS = "DOWNLOAD_TEMPLATE_SUCCESS";
export const DOWNLOAD_TEMPLATE_FAILURE = "DOWNLOAD_TEMPLATE_FAILURE";

export const SET_UPLOAD_PROGRESS = "SET_UPLOAD_PROGRESS";

// Create Offense
export const createOffense = (data) => async (dispatch) => {
  dispatch({ type: CREATE_OFFENSE_REQUEST });
  const token = localStorage.getItem("token");

  try {
    const response = await axios.post(
      `${BASE_URL}/v1/api/createOffense`,
      data,
      { headers: { authorization: token } },
    );

    if (response.data.meta.status) {
      dispatch({ type: CREATE_OFFENSE_SUCCESS, payload: response.data.data });
      return response.data;
    } else {
      dispatch({ type: CREATE_OFFENSE_FAILURE });
      throw new Error(response.data.meta.message);
    }
  } catch (error) {
    dispatch({ type: CREATE_OFFENSE_FAILURE });
    toast.error(
      error.response?.data?.meta?.message || "Failed to create offense",
    );
    throw error;
  }
};

// Fetch Offenses
export const fetchOffenses =
  (page = 1, limit = 10, filters = {}) =>
  async (dispatch) => {
    dispatch({ type: FETCH_OFFENSES_REQUEST });
    const token = localStorage.getItem("token");

    try {
      let queryParams = `?page=${page}&limit=${limit}`;
      if (filters.district_id)
        queryParams += `&district_id=${filters.district_id}`;
      if (filters.municipality_id)
        queryParams += `&municipality_id=${filters.municipality_id}`;
      if (filters.search)
        queryParams += `&search=${encodeURIComponent(filters.search)}`;

      const response = await axios.get(
        `${BASE_URL}/v1/api/admin/getAllOffenses${queryParams}`,
        { headers: { authorization: token } },
      );

      if (response.data.meta.status) {
        dispatch({
          type: FETCH_OFFENSES_SUCCESS,
          payload: {
            data: response.data.data,
            pagination: response.data.meta.pagination,
          },
        });
        return response.data;
      } else {
        dispatch({ type: FETCH_OFFENSES_FAILURE });
        throw new Error(response.data.meta.message);
      }
    } catch (error) {
      dispatch({ type: FETCH_OFFENSES_FAILURE });
      toast.error(
        error.response?.data?.meta?.message || "Failed to fetch offenses",
      );
      throw error;
    }
  };

// Update Offense
export const updateOffense = (id, data) => async (dispatch) => {
  dispatch({ type: UPDATE_OFFENSE_REQUEST });
  const token = localStorage.getItem("token");

  try {
    const response = await axios.put(
      `${BASE_URL}/v1/api/updateOffense/${id}`,
      data,
      { headers: { authorization: token } },
    );

    if (response.data.meta.status) {
      dispatch({ type: UPDATE_OFFENSE_SUCCESS, payload: response.data.data });
      return response.data;
    } else {
      dispatch({ type: UPDATE_OFFENSE_FAILURE });
      throw new Error(response.data.meta.message);
    }
  } catch (error) {
    dispatch({ type: UPDATE_OFFENSE_FAILURE });
    toast.error(
      error.response?.data?.meta?.message || "Failed to update offense",
    );
    throw error;
  }
};

// Delete Offense
export const deleteOffense = (id) => async (dispatch) => {
  dispatch({ type: DELETE_OFFENSE_REQUEST });
  const token = localStorage.getItem("token");

  try {
    const response = await axios.delete(
      `${BASE_URL}/v1/api/deleteOffense/${id}`,
      { headers: { authorization: token } },
    );

    if (response.data.meta.status) {
      dispatch({ type: DELETE_OFFENSE_SUCCESS, payload: id });
      return response.data;
    } else {
      dispatch({ type: DELETE_OFFENSE_FAILURE });
      throw new Error(response.data.meta.message);
    }
  } catch (error) {
    dispatch({ type: DELETE_OFFENSE_FAILURE });
    toast.error(
      error.response?.data?.meta?.message || "Failed to delete offense",
    );
    throw error;
  }
};

// Upload Offenses
export const uploadOffense = (formData) => async (dispatch) => {
  dispatch({ type: UPLOAD_OFFENSE_REQUEST });
  const token = localStorage.getItem("token");

  try {
    const response = await axios.post(
      `${BASE_URL}/v1/api/uploadOffenses`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          authorization: token,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          dispatch({ type: SET_UPLOAD_PROGRESS, payload: percentCompleted });
        },
      },
    );

    if (response.data.meta.status) {
      dispatch({ type: UPLOAD_OFFENSE_SUCCESS, payload: response.data.data });
      dispatch({ type: SET_UPLOAD_PROGRESS, payload: 0 });
      return response.data;
    } else {
      dispatch({ type: UPLOAD_OFFENSE_FAILURE });
      throw new Error(response.data.meta.message);
    }
  } catch (error) {
    dispatch({ type: UPLOAD_OFFENSE_FAILURE });
    dispatch({ type: SET_UPLOAD_PROGRESS, payload: 0 });
    toast.error(
      error.response?.data?.meta?.message || "Failed to upload offenses",
    );
    throw error;
  }
};

// Download Template
export const downloadTemplate = () => async (dispatch) => {
  dispatch({ type: DOWNLOAD_TEMPLATE_REQUEST });

  try {
    const s3Url =
      "https://e-attendance-ebs.s3.ap-south-1.amazonaws.com/uploads/offense-template.xlsx";
    window.open(s3Url, "_blank");
    dispatch({ type: DOWNLOAD_TEMPLATE_SUCCESS });
    toast.success("Template downloading...");
  } catch (error) {
    dispatch({ type: DOWNLOAD_TEMPLATE_FAILURE });
    toast.error("Failed to download template. Please try again.");
    throw error;
  }
};
